import { useContext, useState } from 'react';

import { AssetContext } from '@pages/Asset/Asset.query';

import { ITag } from '@models/Tag.model';
import { removeTags as removeTagsQuery } from '@services/queries/tags';
import { useMutation, useQueryClient } from 'react-query';
import { AssetSidebarTagDialogWithList } from '@components/TagDialog/helpers/AssetSidebarTagDialogWithList';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { Icon } from '@blueprintjs/core';
import { GroupableTags } from '@components/TagDialog/TagDialog.types';

import COLORS_FLAT from '@styles/colors/themes/beam/beam-flat';
import { AddTag, Container, Done, NoTags } from './Tags.styles';

import { TagInfo } from '../TagInfo/TagInfo';
import { calcTagProps } from './Tags.utils';
import { AssetMobileTagList } from './AssetMobileTagList/AssetMobileTagList';
import { Tag } from './AssetMobileTagList/Tag';
import { TagListCount } from './AssetMobileTagList/AssetMobileTagList.styles';

export const Tags = () => {
  const [openTagInf, setOpenTagInf] = useState<boolean>(false);
  const [selectedDelete, setSelectedDelete] = useState<string[]>([]);
  const [activeTag, setActiveTag] = useState<ITag | undefined>(undefined);

  const queryClient = useQueryClient();

  const { equipmentQuery, removeTags, openTagModal, showAllTags } =
    useContext(AssetContext);

  const mutationRemoveTag = useMutation(removeTagsQuery, {
    onSuccess: () => {
      queryClient.invalidateQueries(['equipments', equipmentQuery?.data?.id]);
      removeTags.set(false);
      setSelectedDelete([]);
    },
  });

  const { isDesktop, isMobile } = useSelector(checkDevice());

  const equipment = equipmentQuery?.data;

  if (!equipment) {
    return <div />;
  }

  const hasNoTags = equipment.tags.length === 0;
  const hasTags = !hasNoTags;
  const { tags } = equipment;

  function getTagLimit() {
    if (removeTags.get()) return tags.length;
    if (showAllTags.get()) return tags.length;
    return 2;
  }
  const LIMIT = getTagLimit();
  const twotags = tags.slice(0, LIMIT);
  const rest = tags.slice(LIMIT);
  const showCount = rest.length > 0;

  const getTagClassName = (tag: GroupableTags) => {
    if (removeTags.get() && selectedDelete.includes(tag.id)) {
      return 'deleted';
    }
    return undefined;
  };
  const getTagOnClick = (tag: GroupableTags) => {
    if (removeTags.get()) {
      // Delete tag fn
      return () => {
        if (selectedDelete.find(item => item === tag.id)) {
          setSelectedDelete(selectedDelete.filter(item => item !== tag.id));
        } else {
          setSelectedDelete([...selectedDelete, tag.id]);
        }
      };
    }

    return undefined;
  };
  const getTagIcon = (tag: GroupableTags) => {
    const isSelected = selectedDelete.includes(tag.id);
    if (removeTags.get()) {
      return (
        <Icon icon="delete" color={COLORS_FLAT[isSelected ? 'r400' : 'n600']} />
      );
    }
    return null;
  };
  const getRightSection = () => {
    if (removeTags.get()) {
      return (
        <Done
          underline
          text="b200"
          textColor="b401"
          onClick={() => {
            if (selectedDelete.length > 0) {
              mutationRemoveTag.mutate({
                equipmentId: equipment.id,
                tagsIds: selectedDelete,
              });
            } else {
              removeTags.set(false);
            }
          }}
        >
          Done
        </Done>
      );
    }

    return (
      showCount && (
        <TagListCount
          text="b200"
          textColor="n600"
          onClick={() => {
            showAllTags.set(true);
          }}
        >
          +{rest.length}
        </TagListCount>
      )
    );
  };

  return (
    <Container>
      {isDesktop && (
        <AssetSidebarTagDialogWithList
          tags={tags}
          calcTagProps={calcTagProps({
            removeTags,
            selectedDelete,
            setSelectedDelete,
            setActiveTag,
            setOpenTagInf,
            tags,
          })}
          disableDialog={removeTags.get()}
        />
      )}
      {isMobile && hasTags && (
        <AssetMobileTagList
          tags={twotags}
          renderTag={tag => (
            <Tag
              className={getTagClassName(tag)}
              disabled={!removeTags.get()}
              name={tag.tagName}
              color={tag.color}
              key={tag.id}
              onClick={getTagOnClick(tag)}
              icon={getTagIcon(tag)}
            />
          )}
          rightSection={getRightSection()}
        />
      )}

      {hasNoTags && (
        <NoTags>
          This asset has no tags.{' '}
          <AddTag onClick={() => openTagModal.set(true)}>Add a Tag</AddTag>
        </NoTags>
      )}
      {isDesktop && removeTags.get() && (
        <Done
          mt={16}
          underline
          text="b200"
          textColor="b401"
          onClick={() => {
            if (selectedDelete.length > 0) {
              mutationRemoveTag.mutate({
                equipmentId: equipment.id,
                tagsIds: selectedDelete,
              });
            } else {
              removeTags.set(false);
            }
          }}
        >
          Done
        </Done>
      )}
      <TagInfo
        open={openTagInf}
        onClose={() => {
          setOpenTagInf(false);
        }}
        tag={activeTag}
      />
    </Container>
  );
};
